import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const ABOUT_IMG = "/images/smart-cooler-about.656w.25e29a28.webp";

const CHAPTERS = [
  {
    num: "01",
    title: "Who We Are",
    body: "Vortex Vending Services LLC is a modern vending operator built for today's workplaces. We started with a simple observation: vending has barely changed in decades, but the people using it have. We are here to close that gap — one smart cooler at a time.",
  },
  {
    num: "02",
    title: "What We Do",
    body: "We deploy and service smart coolers — connected, cashless, grab-and-go machines stocked with drinks and snacks. Live inventory telemetry means we know what your location needs before you have to ask. More vending formats are on our roadmap as we grow.",
  },
  {
    num: "03",
    title: "How We Work",
    body: "Three rules run everything: fast communication, fully stocked machines, and spotless amenities. When you message us, a real person responds within one business day. When something runs low, we already know. When we visit, we leave the machine cleaner than we found it.",
  },
  {
    num: "04",
    title: "Where We Are Going",
    body: "Smart Coolers are our first chapter, not our last. We are building a full modern vending platform, shaped directly by the locations and customers we serve. Your feedback literally steers what we build next.",
  },
];

const About = () => (
  <div data-testid="about-page">
    <section className="relative overflow-hidden pt-36 pb-20 lg:pt-48 lg:pb-28">
      <div aria-hidden="true" className="absolute -top-32 right-0 h-[28rem] w-[28rem] rounded-full bg-vortex-cyan/10 blur-[130px]" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <MaskedLine delay={0.1}>
          <Overline>About Us — West Chester, PA Smart Cooler Vending</Overline>
        </MaskedLine>
        <h1 className="mt-8 font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-[6rem]" data-testid="about-heading">
          <MaskedLine delay={0.25}>BUILT FOR THE</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-vortex-cyan">BREAKROOM</span> OF
          </MaskedLine>
          <MaskedLine delay={0.55}>TOMORROW.</MaskedLine>
        </h1>
        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <Reveal delay={0.65} className="lg:col-span-7">
            <div className="clip-frame overflow-hidden border border-white/10 glow-cyan">
              <img src={ABOUT_IMG} alt="Vortex smart cooler with display screen and card reader" width={656} height={1624} loading="lazy" className="h-80 w-full object-cover lg:h-[26rem]" data-testid="about-hero-image" />
            </div>
          </Reveal>
          <Reveal delay={0.75} className="lg:col-span-5">
            <div className="flex h-full flex-col justify-end border-l-2 border-vortex-cyan/50 pl-8">
              <p className="text-lg leading-relaxed text-slate-300">
                Vending used to mean stale rows behind cloudy glass. We think your
                workplace deserves better — connected machines, fresh stock, and a
                team that actually picks up.
              </p>
              <p className="mt-6 font-mono text-xs tracking-[0.25em] text-vortex-cyan uppercase">
                — Vortex Vending Services LLC
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <Marquee />

    <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="manifesto-section">
      {CHAPTERS.map((c, i) => (
        <Reveal key={c.num} delay={0.05 * i}>
          <div
            className="grid grid-cols-1 gap-6 border-t border-white/10 py-16 lg:grid-cols-12 lg:gap-12 lg:py-24"
            data-testid={`chapter-${c.num}`}
          >
            <div className="lg:col-span-3">
              <span className="lg:sticky lg:top-32 block font-heading text-6xl font-black tracking-tighter text-stroke-cyan lg:text-8xl">
                {c.num}
              </span>
            </div>
            <div className="lg:col-span-4">
              <h2 className="font-heading text-3xl font-bold tracking-tight text-white lg:text-4xl">
                {c.title}
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-base leading-relaxed text-slate-400 lg:text-lg">{c.body}</p>
            </div>
          </div>
        </Reveal>
      ))}
      <div className="border-t border-white/10" />

      <Reveal>
        <div className="mt-20 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/5 lg:grid-cols-4" data-testid="about-stats-grid">
          {[
            ["1 business day", "Response standard"],
            ["Live", "Inventory telemetry"],
            ["Every visit", "Full clean-down"],
            ["Growing", "Product roadmap"],
          ].map(([stat, label]) => (
            <div key={label} className="bg-vortex-ink p-10 transition-colors duration-500 hover:bg-vortex-surface">
              <p className="font-heading text-2xl font-bold text-vortex-cyan lg:text-3xl">{stat}</p>
              <p className="mt-2 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase">{label}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  </div>
);

export default About;
